import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ArrowRight } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { itemVariants } from '../shared/animations';
import { MODULES, getModuleIcon, getFamilyDefinition } from '../../content';
import { getModuleFromQuestionId } from '../../utils/activityHelpers';
import { getGrowthStage } from '../../mastery';
interface GrowthThisWeekSectionProps {
  state: AppState;
  selectedGroup: GroupId;
  onNavigate: (view: string) => void;
}
export function GrowthThisWeekSection({
  state,
  selectedGroup,
  onNavigate
}: GrowthThisWeekSectionProps) {
  const topGrowth = useMemo(() => {
    if (!state.familyMastery) return [];
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const groupSuffix = `_g${selectedGroup}`;
    const growthList: Array<{
      familyId: string;
      familyName: string;
      moduleId: string;
      delta: number;
      currentScore: number;
    }> = [];
    Object.entries(state.familyMastery).forEach(([key, familyState]) => {
      if (!key.endsWith(groupSuffix) || !familyState.history) return;
      let recentCorrect = 0;
      familyState.history.forEach(attempt => {
        if (attempt.timestamp >= weekAgo && attempt.isCorrect) {
          recentCorrect++;
        }
      });
      if (recentCorrect > 0) {
        const def = getFamilyDefinition(familyState.familyId);
        const familyName = def?.description || def?.learningGoal || familyState.familyId;
        const moduleId = getModuleFromQuestionId(familyState.familyId) || 'Basisrekenen';
        growthList.push({
          familyId: familyState.familyId,
          familyName,
          moduleId,
          delta: recentCorrect,
          currentScore: familyState.masteryScore
        });
      }
    });
    return growthList.sort((a, b) => b.delta - a.delta).slice(0, 3);
  }, [state.familyMastery, selectedGroup]);
  return <div className="space-y-4" data-id="element-1880">
      <div className="flex items-center gap-3" data-id="element-1881">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-1882">
          <TrendingUp className="w-5 h-5 text-emerald-600" data-id="element-1883" />
        </div>
        <h2 className="text-xl font-black text-slate-800" data-id="element-1884">
          Deze week gegroeid
        </h2>
      </div>

      {topGrowth.length === 0 ? <Card className="p-6 text-center bg-slate-50 border-dashed border-2 border-slate-200 rounded-2xl shadow-sm" data-id="element-1885">
          <p className="text-slate-500 font-medium mb-3" data-id="element-1886">
            Begin een sessie om hier je groei te zien! 🌱
          </p>
          <button onClick={() => onNavigate('modules')} className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-xl font-bold hover:bg-emerald-200 transition-colors" data-id="element-1887">
          
            Naar Mijn Werelden <ArrowRight className="w-4 h-4" data-id="element-1888" />
          </button>
        </Card> : <div className="flex sm:grid sm:grid-cols-3 overflow-x-auto pb-4 -mb-4 sm:pb-0 sm:mb-0 gap-3 sm:gap-4 snap-x snap-mandatory scrollbar-hide -mx-3 px-3 sm:mx-0 sm:px-0" data-id="element-1889">
          {topGrowth.map((item, i) => {
        const moduleDef = MODULES.find(m => m.id === item.moduleId);
        const ModuleIcon = moduleDef ? getModuleIcon(moduleDef.icon) : TrendingUp;
        const growthStage = getGrowthStage(item.currentScore);
        // Map module to a color class for the left border
        const colorMap: Record<string, string> = {
          Metriek: 'border-l-blue-500',
          Getalbegrip: 'border-l-indigo-500',
          Basisrekenen: 'border-l-orange-500',
          Tijd: 'border-l-sky-500',
          Geld: 'border-l-yellow-500',
          SpellingWW: 'border-l-fuchsia-500',
          SpellingRegel: 'border-l-amber-500',
          Taalbegrip: 'border-l-purple-500',
          Studievaardig: 'border-l-teal-500',
          Topo: 'border-l-cyan-500'
        };
        const borderClass = colorMap[item.moduleId] || 'border-l-emerald-500';
        return <motion.div key={item.familyId} variants={itemVariants} className="min-w-[78%] sm:min-w-0 snap-center flex-shrink-0 sm:flex-shrink" data-id="element-1890">
              
                <Card className={`p-4 h-full border-2 border-slate-100 border-l-4 ${borderClass} bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow flex flex-col`} data-id="element-1891">
                
                  <div className="flex items-start justify-between gap-2 mb-2" data-id="element-1892">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0" data-id="element-1893">
                      <ModuleIcon className="w-4 h-4 text-slate-600" data-id="element-1894" />
                    </div>
                    <div className="flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-lg" data-id="element-1895">
                      <span className="text-xs font-black" data-id="element-1896">{item.delta}×</span>
                      <span className="text-[10px] font-bold uppercase" data-id="element-1897">
                        goed
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 min-w-0 mb-3" data-id="element-1898">
                    <h4 className="font-bold text-slate-800 text-sm leading-tight line-clamp-2" data-id="element-1899">
                      {item.familyName}
                    </h4>
                  </div>

                  <div className="flex items-center gap-2 mt-auto pt-3 border-t border-slate-50" data-id="element-1900">
                    <span className="text-lg" data-id="element-1901">{growthStage.emoji}</span>
                    <span className="text-xs font-bold text-slate-500" data-id="element-1902">
                      {growthStage.label}
                    </span>
                  </div>
                </Card>
              </motion.div>;
      })}
        </div>}
    </div>;
}